/**
 * stubData — every placeholder in the Strategy Automation tab lives HERE and
 * nowhere else, so what's real vs. stubbed is auditable at a glance.
 *
 * COMPLIANCE RULE: anything from this file must be rendered under an explicit
 * "Preview" / "placeholder" label. Never present these values as member data.
 *
 * Real data comes exclusively from services/signalsApi.js (/status, /enroll,
 * /delegate-address, /report) and the member's own wallet via viem.
 */

/* Shown wherever stub data is rendered. Keep the wording blunt. */
export const PLACEHOLDER_LABEL = "Preview — illustrative placeholder, not your data";

/* The one phrase for anything on this tab that came out of the local model
   instead of the engine. Rendered verbatim next to those values, and by
   EngineModeChip whenever the tab is in demo mode (SUPERBETA-7). */
export const DEMO_DATA_LABEL = "Demo data — engine not connected";

/* Its counterpart. Once the engine has answered, the figures around it are the
   member's own and saying so is as important as labelling the demo ones —
   "unlabelled" is not a state a live financial surface gets to have. */
export const LIVE_DATA_LABEL = "Live — engine values";

/* ── Supernova staking gate — NO LONGER STUBBED AT ALL ──────────────────
   ARC-32 made the gate live off the staking contract; ARC-53 moved it to the
   engine's member-level slot pool (GET /status.staking), which sums the stake
   across every verified wallet on the account rather than the one connected
   here.

   STUB_SUPERNOVA IS GONE, AND MUST NOT COME BACK. It was the fallback for the
   states where the count was not a fact — no wallet, wrong network, failed
   read — and in every one of them it UNLOCKED rows on a number nobody had
   verified. With no engine-side gate behind it (ARC-43 unbuilt) that could let
   a member configure allocations their stake does not back. Those states now
   lock and say so: see slotGate.js.

   TODO(backend, supervised): the engine still does not enforce the gate at
   signal time. Nothing on this tab may say a signal was blocked by it. */

/* ── Multi-wallet model ──────────────────────────────────────────────────
   MULTI_WALLET_AVAILABLE IS GONE. It was the flag that kept "Add wallet"
   disabled while the engine was single-wallet, and the reason no fake second
   wallet card was ever rendered in its place. ARC-41 built the registry and
   ARC-46 wired the tab to it: the wallet list is now the engine's own
   GET /wallets, every row is a wallet it has verified, and there is nothing
   left here to stub. See useWalletRegistry.js + WalletRegistryPanel.js. */

/* ── Strategy rows ───────────────────────────────────────────────────────
   The pairs offered as automatable strategies. Venue pair names only — not
   recommendations. Since #9, allocation/leverage values SEED the rows only
   until a backend riskConfig arrives — then WalletSection re-seeds from the
   engine's saved /status.riskConfig.allocations and Save persists edits back.
   As of the 2026-08-12 brief allocation sizing is DEPLOYED server-side: saved
   allocations DO size trades for accounts in sizingMode "allocation".
   TODO(backend, supervised): Supernova gate enforced server-side. */
export const STUB_STRATEGIES = [
  { pair: "BTC/USD", allocationPct: 30, leverage: 2.0 },
  { pair: "ETH/USD", allocationPct: 20, leverage: 1.0 },
  { pair: "SOL/USD", allocationPct: 0, leverage: 1.0 },
];

export const MAX_LEVERAGE = 2;

/* ── Pending-exit heads-up preview (#23 Phase 1) ─────────────────────────
   TODO(backend, supervised): nothing behind this exists yet — no notification
   endpoint, no delivery channel, no stored opt-in. The "Exit heads-ups" card is a
   flag-gated PREVIEW: its toggle lives in local component state and persists
   nothing, and the single payload below is the ONLY heads-up ever rendered.
   It is example data, never a member's position.
   TODO(engine contract): these field names are provisional. When the engine
   ratifies the real pending-exit contract, rewire PendingExitHeadsUp.js (the
   one small component that reads this shape) and delete this stub. */
export const STUB_PENDING_EXIT_EXAMPLE = {
  pair: "BTC/USD",
  side: "Long",
  reason: "Your signal source flagged its exit condition",
  // Fixed string on purpose: an example never renders a live "now".
  time: "14:32 UTC",
};

/* ── Phase-2 reporting — NO LONGER STUBBED ───────────────────────────────
   The performance strip + growth chart now render from the engine's real
   GET /report (see reportModel.js / useEngineReport.js). The old
   STUB_PERFORMANCE dataset was removed with it; when the endpoint is
   unavailable the section shows an honest empty state, never placeholder
   numbers. */
